import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { userAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Card } from '../../components/ui/card';
import { Dumbbell, RotateCcw, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

export function PracticePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const topicId = params.get('topic_id') || undefined;

  const [questions, setQuestions] = useState([]);
  const [topicName, setTopicName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setIdx(0); setPicked(null); setScore(0); setFinished(false);
    try {
      const lang = (i18n.language || 'en').split('-')[0];
      const res = await userAPI.getPracticeQuestions({ topic_id: topicId, count: 10, language: lang });
      setQuestions(res.data.questions || []);
      setTopicName(res.data.topic_name);
      if (!res.data.questions?.length) toast.info(t('practice.empty', 'No questions available yet'));
    } catch (e) {
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [topicId, i18n.language, t]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner className="py-20" />;

  const q = questions[idx];
  const progressPct = questions.length ? Math.round(((idx + (picked ? 1 : 0)) / questions.length) * 100) : 0;

  const choose = (key) => {
    if (picked) return;
    setPicked(key);
    if (key === q.correct_key) setScore(s => s + 1);
  };

  const next = () => {
    if (idx + 1 >= questions.length) { setFinished(true); return; }
    setIdx(i => i + 1);
    setPicked(null);
  };

  if (finished || (!q && questions.length > 0)) {
    const pct = questions.length ? Math.round((score / questions.length) * 100) : 0;
    return (
      <div className="max-w-xl mx-auto px-4 pb-10">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
          <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-8 text-center">
            <div className="text-5xl mb-3">{pct >= 80 ? '🎉' : pct >= 50 ? '💪' : '📚'}</div>
            <h1 className="text-2xl font-bold text-white mb-1">{t('practice.done', 'Practice complete!')}</h1>
            <p className="text-slate-400 mb-6">{topicName || t('practice.mixed', 'Mixed topics')}</p>
            <p className="text-4xl font-bold text-teal-400 mb-6">{score}/{questions.length} <span className="text-lg text-slate-400">({pct}%)</span></p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={load} className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl">
                <RotateCcw className="w-4 h-4 mr-2" /> {t('practice.again', 'Practice again')}
              </Button>
              <Button onClick={() => navigate('/learning')} variant="outline" className="rounded-xl bg-transparent border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white">
                {t('practice.backToLearning', 'Back to Learning Path')}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-5">{t('practice.noXp', 'Practice mode: unlimited tries, no XP, not counted in rankings.')}</p>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (!q) {
    return (
      <div className="max-w-xl mx-auto px-4 text-center py-16">
        <Dumbbell className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <p className="text-slate-400">{t('practice.empty', 'No questions available yet')}</p>
        <Button onClick={() => navigate('/learning')} className="mt-4" variant="outline">{t('practice.backToLearning', 'Back to Learning Path')}</Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 pb-10 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Dumbbell className="w-5 h-5 text-teal-400" /> {t('practice.title', 'Practice Mode')}
          </h1>
          <p className="text-xs text-slate-500">{topicName || t('practice.mixed', 'Mixed topics')} • {t('practice.noXp', 'Practice mode: unlimited tries, no XP, not counted in rankings.')}</p>
        </div>
        <span className="text-sm text-slate-400 font-['Azeret_Mono']">{idx + 1}/{questions.length}</span>
      </div>

      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-teal-400 to-cyan-400 transition-all" style={{ width: `${progressPct}%` }} />
      </div>

      <motion.div key={q._id} initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }}>
        <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-5 sm:p-6">
          <span className="inline-block text-xs px-2.5 py-1 rounded-full bg-teal-500/15 text-teal-300 mb-3">{q.topic_name}</span>
          <h2 className="text-lg font-semibold text-white mb-5">{q.text}</h2>
          <div className="space-y-3">
            {q.options.map(opt => {
              const isCorrect = picked && opt.key === q.correct_key;
              const isWrongPick = picked === opt.key && opt.key !== q.correct_key;
              return (
                <button
                  key={opt.key}
                  onClick={() => choose(opt.key)}
                  disabled={!!picked}
                  className={`w-full flex items-center gap-3 text-left px-4 py-3.5 rounded-xl border transition-all ${
                    isCorrect ? 'border-emerald-500 bg-emerald-500/15 text-emerald-200'
                    : isWrongPick ? 'border-rose-500 bg-rose-500/15 text-rose-200'
                    : picked ? 'border-slate-800 bg-slate-800/40 text-slate-400'
                    : 'border-slate-700 bg-slate-800/60 text-slate-200 hover:border-teal-500/60 hover:bg-slate-800'
                  }`}
                >
                  <span className="w-7 h-7 shrink-0 rounded-lg bg-slate-700/80 flex items-center justify-center text-xs font-bold">{opt.key}</span>
                  <span className="flex-1">{opt.label}</span>
                  {isCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
                  {isWrongPick && <XCircle className="w-5 h-5 text-rose-400" />}
                </button>
              );
            })}
          </div>
          {picked && (
            <div className="mt-5 flex justify-end">
              <Button onClick={next} className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl">
                {idx + 1 >= questions.length ? t('practice.finish', 'Finish') : t('practice.next', 'Next')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
